import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

type UseEditModeOptions = {
  listingId: string;
  creatorIds?: string[];
};

type UseEditModeResult = {
  isEditMode: boolean;
  canEdit: boolean;
  toggleEditMode: () => void;
  enterEditMode: () => void;
  exitEditMode: () => void;
};

export function useEditMode({
  listingId,
  creatorIds = [],
}: UseEditModeOptions): UseEditModeResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const isEditMode = searchParams.get("editing") === "true";

  const isOwner = user?.primaryListingId === listingId;
  const isCreator = creatorIds.includes(user?.primaryListingId || "");
  const canEdit = isOwner || isCreator;

  const updateEditParam = useCallback(
    (editing: boolean) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (editing) {
            params.set("editing", "true");
          } else {
            params.delete("editing");
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const toggleEditMode = useCallback(() => {
    updateEditParam(!isEditMode);
  }, [isEditMode, updateEditParam]);

  const enterEditMode = useCallback(() => {
    updateEditParam(true);
  }, [updateEditParam]);

  const exitEditMode = useCallback(() => {
    updateEditParam(false);
  }, [updateEditParam]);

  return {
    isEditMode: isEditMode && canEdit,
    canEdit,
    toggleEditMode,
    enterEditMode,
    exitEditMode,
  };
}
